import joi, { optional } from '@hapi/joi';
import {
  AccountHolderDetails,
  AddressDetails,
  CountryOfFiscalResidence,
  DisbursementAccountDetails,
  FeesAndCharges,
  OpenAccountInvestmentProfile,
  OpenAccountInvestmentProfileIncomeSource,
  OpenAccountInvestmentProfilePreviousExperience,
  OpenAccountMetadataOption,
  OpenAccountPersonalDetails,
} from './types';

import { getAccountType } from '../../store/openAccount/support';


const errorHelper = (error: any, name: string, accountHolder = '') => {
  return error.map((err: any) => {
    switch (err.type) {
      case 'any.empty':
        return  { message: `${accountHolder} ${name} field is required`};
      case 'string.min':
        return {
          message:
            `${accountHolder} ${name} must have a min character length of ${err.context.limit}`,
        };
      case 'string.max':
        return {
          message:
            `${accountHolder} ${name} can only have a max character length of ${err.context.limit}`,
        };
      case 'string.length':
        return {
          message:
            `${accountHolder} ${name} must be ${err.context.limit} characters long`,
        };
      case 'string.email':
        return {
          message:
            `${accountHolder} ${name} must be a valid email`,
        };
      case 'string.base':
        return {
          message:
            `${accountHolder} ${name} must be a valid string`,
        };
      default:
        return err;
    }
  });
};

export const DropdownOptionSchema = joi
  .object<OpenAccountMetadataOption>()
  .unknown(true)
  .keys({
    value: joi.any().required(),
    text: joi.string().optional(),
    metadata: joi.any().optional(),
  })
  .empty(null);

export const PasswordSchema = joi
  .object<{ password: string; confirmPassword: string }>()
  .unknown(true)
  .keys({
    password: joi.string().min(8).required(),
    confirmPassword: joi.string().required().valid(joi.ref('password'))
    .error((err: any) => 'Please ensure the value entered in the "Confirm Password" Field matches the "New Password" Field'),
  })
  .empty(null);


interface SourceOfFund {
    value: OpenAccountMetadataOption;
    proceeds: string;
    remarks: string;
    SelfEmployedRemarks: string;
    RetiredRemarks: string;
    FundTypeId?: number;
    NetAssetsRangeId?: number;
    IncomeSaleId?: number;
    Proceeds?: string;
    Remarks?: string;
  }


interface IncomeSource {
    value: OpenAccountMetadataOption;
    proceeds: string;
    remarks: string;
  }

export const IncomeSource =  joi
  .object<SourceOfFund>()
  .keys({
      value: DropdownOptionSchema.optional().error(
        (err: any) => 'Please enter an income source',
      ),
      proceeds: joi
      .optional()
      .error((err: any) => 'Please enter an income source'),
      remarks: joi
      .optional()
      .error((err: any) => 'Please enter an income source'),
  });

const savingChoice = {
    value: 1364,
    text: 'Savings from salary/wages/commissions/royalties',
    metadata: {},
  };
const salesChoice = {
    value: 1365,
    text: 'Proceeds from sales',
    metadata: {},
  };
const otherChoice = {
    value: 1366,
    text: 'Other',
    metadata: {},
  };

export const SourceOfFundSchema = (isSelfEmployed: boolean, isRetired: boolean) => joi
  .object<SourceOfFund>()
  .unknown(true)
  .keys({
      value: DropdownOptionSchema.optional().error(
        (err: any) => 'Currency field is required',
      ),
      proceeds: joi.when('value', {
        is: joi.object( salesChoice ),
        then: joi.string().required().error(() => 'Please also specify an amount in your selected proceeds from sales'),
      }),
      remarks: joi
      .any()
      .optional()
      .error((err: any) => 'Please specify the amount of source of fund'),
      SelfEmployedRemarks: isSelfEmployed ? joi
      .when('value', {
        is: joi.object( savingChoice ),
        then: joi.string().required().error(() => 'Please enter Self Employed Remarks '),
      }) : joi.optional(),
      RetiredRemarks: isRetired ? joi
      .when('value', {
        is: joi.object( savingChoice ),
        then: joi.string().required().error(() => 'Please enter retired remarks '),
      }) : joi.optional(),
      IncomeSaleId: joi
      .when('value', {
        is: joi.object( otherChoice ),
        then: joi.number().required().error(() => 'Please choose the type of fund '),
      }),
  });

export const DisbursementAccountSchema = joi
  .object<DisbursementAccountDetails>()
  .unknown(true)
  .keys({
    currencyId: DropdownOptionSchema.required().error(
      (err: any) => 'Currency field is required',
    ),
    name: joi
      .string()
      .required()
      .max(128)
      .error((err: any) => errorHelper(err, 'Bank Account Name')),
    iban: joi
      .string()
      .regex(/^[\w-_.]*$/)
      .required()
      .max(128)
      .error((err: any) => errorHelper(err, 'Iban')),
    bankSortCode: joi
      .string()
      .required()
      .max(128)
      .error((err: any) => errorHelper(err, 'Bank Sort/SWIFT Code')),
      bankName: joi
      .string()
      .required()
      .max(128)
      .error((err: any) => errorHelper(err, 'Bank Name')),
    country: DropdownOptionSchema.required().error(
      (err: any) => 'Bank Account Country field is required',
    ),
  });

export const AddressSchema = (accountHolder = '') => joi
  .object<AddressDetails>()
  .unknown(true)
  .keys({
    address: joi
      .string()
      .required()
      .max(256)
      .error((err: any) => errorHelper(err, 'Primary Address', accountHolder)),
    suburb: joi
      .string()
      .required()
      .max(24)
      .error((err: any) => errorHelper(err, 'Place/Suburb', accountHolder)),
    postcode: joi
      .string()
      .required()
      .max(10)
      .error((err: any) => errorHelper(err, 'Postcode', accountHolder)),

      number: joi
      .string()
      .max(8)
      .error((err: any) => errorHelper(err, 'Street Number', accountHolder)),
    country: DropdownOptionSchema.required().error(
      (err: any) => accountHolder + ' ' + 'Address field is required',
    ),
  });

export const AccountHolderSchema = (accountType?: number, accountHolder?: string ) => {
  // ACCONUT TYPE
  let account = null;
  if (accountType) {
    account = getAccountType(accountType);
  }

  return joi
    .object<AccountHolderDetails>()
    .unknown(true)
    .keys({
      title: !(account?.coroprate || account?.masterAccount)
        ? DropdownOptionSchema.required().error(
            (err: any) => accountHolder + ' ' + 'Title field is required',
          )
        : DropdownOptionSchema.optional().error(
            (err: any) => accountHolder + ' ' + 'Title field is required',
          ),
      firstName: joi
        .string()
        .optional()
        .max(35)
        .allow('')
        .error((err: any) =>
          errorHelper(err, 'First Name', accountHolder ?? ''),
        ),
      lastName: joi
        .string()
        .required()
        .max(35)
        .error((err: any) =>
          errorHelper(err, 'Company/Last name', accountHolder ?? ''),
        ),
      address: AddressSchema(accountHolder ?? '')
        .required()
        .error((err: any) => 'Address field is required'),
      previousAddress: AddressSchema(accountHolder ?? '')
        .optional()
        .allow(null),
      passportNumber: !(account?.coroprate || account?.masterAccount)
        ? joi
            .string()
            .required()
            .min(6)
            .max(14)
            .error((err: any) =>
              errorHelper(err, 'Passport Number', accountHolder ?? ''),
            )
        : joi.optional(),
      emailAddress: joi
        .string()
        .required()
        .min(6)
        .max(40)
        .email()
        .error((err: any) =>
          errorHelper(err, 'Primary Email', accountHolder ?? ''),
        ),
      secondaryEmailAddress: joi
        .string()
        .optional()
        .min(6)
        .max(40)
        .allow(null, '')
        .email()
        .error((err: any) =>
          errorHelper(err, 'Secondary Email', accountHolder ?? ''),
        ),
      primaryPhoneNumber: joi
        .string()
        .required()
        .min(8)
        .max(20)
        .error((err: any) =>
          errorHelper(err, 'Mobile number', accountHolder ?? ''),
        ),
      primaryCountryCode: joi
        .required()
        .error((err: any) =>
          errorHelper(
            err,
            'Please select country code for number',
            accountHolder ?? '',
          ),
        ),
      secondaryPhoneNumber: joi
        .string()
        .optional()
        .max(20)
        .allow('')
        .error((err: any) =>
          errorHelper(err, 'Secondary Phone', accountHolder ?? ''),
        ),
      fax: joi.string().optional().max(20).allow(''),
      dobDate: joi
        .string()
        .required()
        .length(2)
        .error((err: any) =>
          errorHelper(err, 'Date of Birth Day', accountHolder ?? ''),
        ),
      dobMonth: joi
        .string()
        .required()
        .length(2)
        .error((err: any) =>
          errorHelper(err, 'Date of Birth Month', accountHolder ?? ''),
        ),
      dobYear: joi
        .string()
        .required()
        .length(4)
        .error((err: any) =>
          errorHelper(err, 'Date of Birth Year', accountHolder ?? ''),
        ),
      placeOfBirth: joi
        .string()
        .required()
        .max(32)
        .error((err: any) =>
          errorHelper(err, 'Place of Birth', accountHolder ?? ''),
        ),
      countryOfBirthId: DropdownOptionSchema.required().error(
        (err: any) =>
          accountHolder + ' ' + 'Country of Birth field is required',
      ),
      nationalityId: !(account?.coroprate || account?.masterAccount)
        ? DropdownOptionSchema.required().error(
            (err: any) => accountHolder + ' ' + 'Nationality field is required',
          )
        : DropdownOptionSchema.optional().error(
            (err: any) => accountHolder + ' ' + 'Nationality field is required',
          ),
      maritalStatusId: !(account?.coroprate || account?.masterAccount)
        ? DropdownOptionSchema.required().error(
            (err: any) =>
              accountHolder + ' ' + 'Marital Status field is required',
          )
        : DropdownOptionSchema.optional().error(
            (err: any) =>
              accountHolder + ' ' + 'Marital Status field is required',
          ),
      countryOfFiscalResidence:
        account?.trust || account?.qrops
          ? joi.optional()
          : joi
              .object<CountryOfFiscalResidence>()

              .required()
              .unknown(true)
              .keys({
                country: DropdownOptionSchema.required().error(
                  (err: any) =>
                    accountHolder +
                    ' ' +
                    'Country of Fiscal Residence field is required',
                ),
                tin: joi.string().optional().allow(''),
                reasonForNoTin: joi.when('tin', {
                  is: '',
                  then: DropdownOptionSchema.required().error(
                    (err: any) => 'Reason for no TIN field is required',
                  ),
                  otherwise: DropdownOptionSchema.optional().allow(null),
                }),
              }),

      lei: joi.string().optional().allow(''),
      politicallyExposedPersons: joi
        .bool()
        .required()
        .error(
          (err: any) =>
            accountHolder +
            ' ' +
            'Politically Exposed Person field is required',
        ),
      //
      OccupationId: !(account?.coroprate || account?.masterAccount)
        ? DropdownOptionSchema.optional().error(
            (err: any) => accountHolder + ' ' + 'Occupation field is required',
          )
        : null,
      Occupation: !(account?.coroprate || account?.masterAccount)
        ? joi
            .string()
            .required()
            .max(35)
            .error((err: any) =>
              errorHelper(err, 'Occupation', accountHolder ?? ''),
            )
        : joi.optional(),
      EmploymentStatusId: !(account?.coroprate || account?.masterAccount)
        ? DropdownOptionSchema.required().error(
            (err: any) =>
              accountHolder + ' ' + 'Employment Status Field is required',
          )
        : DropdownOptionSchema.optional().error(
            (err: any) =>
              accountHolder + ' ' + 'Employment Status Field is required',
          ),
      EmployerName: !(account?.coroprate || account?.masterAccount)
        ? joi
            .string()
            .max(25)
            .optional()
            .error((err: any) =>
              errorHelper(err, 'Employer Name', accountHolder ?? ''),
            )
        : joi.optional(),
      //
      EmployerNumber: joi.optional(),
      VATNumber: joi.optional(),
      IsUSCitizen: joi.bool().optional(),
    });
};

export const PersonalDetailsSchema = (accountType: number, hasCorrespondence: boolean) => {
  return joi
    .object<OpenAccountPersonalDetails>()
    .unknown(true)
    .keys({
      primaryAccountHolder: AccountHolderSchema(accountType, 'First Account Holder')
        .required()
        .concat(
          joi.object({
            CorrespondenceTermsAgreed:
              getAccountType(accountType).qrops ||
              getAccountType(accountType).trust
                ? joi.optional()
                : joi
                    .bool()
                    .required()
                    .invalid(false)
                    .error(
                      (err: any) =>
                        'Correspondence Terms must be agreed to before continuing',
                    ),
            ReportingTermsAgreed: joi
              .bool()
              .required()
              .invalid(false)
              .error(
                (err: any) =>
                  'Reporting Terms must be agreed to before continuing',
              ),
          }),
        ),
      secondaryAccountHolder: AccountHolderSchema(accountType, 'Second Account Holder')
        .optional()
        .allow(null),
      alternativeMailingAddress: hasCorrespondence ? AddressSchema().optional().allow(null) : joi.optional(),
    });
};

export const FeesAndChargesSchema = joi
  .object<FeesAndCharges>()
  .unknown(true)
  .keys({
    id: joi.number().optional(),
    platformProcessingFee: joi
      .number()
      .required()
      .error((err: any) => 'Total Processing Fee field is required'),
    annualAccountFeeAdministration: joi
      .number()
      .min(0.1)
      .required()
      .error((err: any) => errorHelper(err, 'Annual Account Fee')),
    annualAccountFeeRenumeration: joi
      .number()
      .min(0.1)
      .required()
      .error((err: any) => errorHelper(err, 'Annual Account Fee Capital Advisory Group (UK)')),
    annualServiceFee: joi
      .number()
      .min(0.1)
      .required()
      .error((err: any) => 'Annual Service Fee (%) field is required'),
    initialFee: joi
      .number()
      .required()
      .error((err: any) => 'Initial Fee field is required'),
    etfEntryFee: joi
      .number()
      .required()
      .error((err: any) => 'ETF Entry Fee field is required'),
    equitiesEntryFee: joi
      .number()
      .required()
      .error((err: any) => 'Equities Entry Fee field is required'),
    minimumCashBalance: joi
      .number()
      .min(0.1)
      .required()
      .error((err: any) => 'Minimum Cash Balance Amount field is required'),
  });

export const IncomeSourceSchema = joi
  .object<OpenAccountInvestmentProfileIncomeSource>()
  .unknown(true)
  .keys({
    value: DropdownOptionSchema.required(),
    proceeds: joi
      .number()
      .required()
      .error((err: any) => 'Amount in local currency is required'),
    remarks: joi.string().optional().allow(''),
  });
export const Appropriatenesses = joi
  .object<OpenAccountInvestmentProfileIncomeSource>()
  .unknown(true)
  .keys({
    value: DropdownOptionSchema.required(),
    proceeds: joi.string().required(),
    remarks: joi.string().optional().allow(''),
  });

export const PreviousExperienceSchema = joi
  .object<OpenAccountInvestmentProfilePreviousExperience>()
  .unknown(true)
  .keys({
    value: DropdownOptionSchema.required(),
    experience: DropdownOptionSchema.required(),
    transactions: DropdownOptionSchema.required(),
    averageVolume: DropdownOptionSchema.required(),
  });

export const InvestmentProfileSchema = (personalDetails: any) => {
  const isSelfEmployed = personalDetails.EmploymentStatusId?.text === 'Self Employed';
  const isRetired = personalDetails.EmploymentStatusId?.text === 'Retired';
  return joi
  .object<OpenAccountInvestmentProfile>()
  .unknown(true)
  .keys({
    id: joi.any().optional(),
    investmentCurrency: DropdownOptionSchema.required().error(
      (err: any) => 'Investment Currency field is required',
    ),
    investmentAmount: joi
      .number()
      .greater(0)
      .required()
      .error((err: any) => 'Investment Amount field is required'),
    SourceOfFunds: joi.array().required().items(SourceOfFundSchema(isSelfEmployed, isRetired)).min(1).error(() => 'Please choose a source of fund'),
    incomeSources: joi.array().required().items(IncomeSource).min(1).error(() => 'Please choose an income source'),
    // appropriatenesses: joi.array().required().items(IncomeSourceSchema),
    investmentProfile: joi
      .number()
      .min(1)
      .strict()
      .required()
      .error((err: any) => 'Risk Profile Category selection is required'),
    NaturalPersonTerms: joi.
    boolean().invalid(false)
    .error((err: any) => 'Please check account for natural person'),
    seekExemption: joi.bool().optional(),
    previousExperience: joi.array().optional().items(PreviousExperienceSchema),
    pastInvestmentServices: joi.array().optional().items(DropdownOptionSchema),
  }); };
